import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DropzoneUploaderProps } from './types';
import { useFileManager } from './hooks/useFileManager';
import { useUploadLogic } from './hooks/useUploadLogic';
import DropzoneArea from './components/DropzoneArea';
import FilePreview from './components/FilePreview';
import UploadProgress from './components/UploadProgress';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';
import useSnackbar from '@/hooks/useSnackbar.tsx';

const DropzoneUploader: React.FC<DropzoneUploaderProps> = ({
  handleUploadFile,
  uploadType,
  existingFiles = [],
  onDeleteExistingFile,
  onSetMainImage,
  maxFiles = 10,
  disabled = false,
  onUploadComplete,
  onUploadError,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { mediaFiles, acceptedFileTypes, canAddMoreFiles, handleDrop, removeFile, clearAllFiles } =
    useFileManager({
      uploadType,
      maxFiles,
      existingFilesCount: existingFiles.length,
      onUploadError: (errorMsg) => {
        showSnackbar({ title: errorMsg, variant: 'error' });
        onUploadError?.(errorMsg);
      },
    });

  const { loading, uploadProgress, uploadFiles } = useUploadLogic({
    handleUploadFile,
    onUploadComplete,
    onUploadError: (errorMsg) => {
      showSnackbar({
        title:
          'Se produjo un error subiendo los archivos. Comprueba que el formato y el tamaño es correcto.',
        variant: 'error',
      });
      onUploadError?.(errorMsg);
    },
  });

  useEffect(() => {
    return () => {
      mediaFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [mediaFiles]);

  const handleUploadClick = useCallback(async () => {
    await uploadFiles(mediaFiles);
    clearAllFiles();
  }, [uploadFiles, mediaFiles, clearAllFiles]);

  const handleClearAll = useCallback(() => {
    clearAllFiles();
  }, [clearAllFiles]);

  const handleDeleteExisting = useCallback((id: string) => {
    setConfirmDeleteId(id);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (confirmDeleteId && onDeleteExistingFile) {
      try {
        await onDeleteExistingFile(confirmDeleteId);
        setConfirmDeleteId(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al eliminar el archivo';
        showSnackbar({ title: errorMessage, variant: 'error' });
        onUploadError?.(errorMessage);
      }
    }
  }, [confirmDeleteId, onDeleteExistingFile, onUploadError, showSnackbar]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteId(null);
  }, []);

  const handleSetMain = useCallback(
    async (id: string) => {
      if (onSetMainImage) {
        try {
          await onSetMainImage(id);
          showSnackbar({ title: 'Imagen principal actualizada', variant: 'success' });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Error al establecer imagen principal';
          showSnackbar({ title: errorMessage, variant: 'error' });
          onUploadError?.(errorMessage);
        }
      }
    },
    [onSetMainImage, showSnackbar, onUploadError]
  );

  const uploadButtonDisabled = loading || mediaFiles.length === 0 || disabled;

  return (
    <Box>
      {/* Dropzone */}
      <DropzoneArea
        acceptedFileTypes={acceptedFileTypes}
        disabled={disabled}
        canAddMoreFiles={canAddMoreFiles}
        uploadType={uploadType}
        mediaFilesLength={mediaFiles.length}
        existingFilesLength={existingFiles.length}
        maxFiles={maxFiles}
        loading={loading}
        uploadProgress={uploadProgress}
        onDrop={handleDrop}
      />

      {/* Existing Files Section */}
      {existingFiles.length > 0 && (
        <Box mb={4}>
          <Typography variant="subtitle2" mb={1}>
            Archivos existentes
          </Typography>
          {uploadType === 'images' && onSetMainImage && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Haz clic en el icono de favorito para establecer la imagen principal
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={2}>
            {existingFiles.map((file) => (
              <FilePreview
                key={file.id}
                file={file}
                type="existing"
                onDelete={
                  onDeleteExistingFile && file.canDelete !== false
                    ? handleDeleteExisting
                    : undefined
                }
                onSetMain={uploadType === 'images' && onSetMainImage ? handleSetMain : undefined}
                isMain={file.isMain}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* New Files Section */}
      {mediaFiles.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" mb={1}>
            Archivos nuevos
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {mediaFiles.map((file) => (
              <FilePreview key={file.id} file={file} type="new" onRemove={removeFile} />
            ))}
          </Box>
        </Box>
      )}

      {/* Upload Progress */}
      <UploadProgress progress={uploadProgress} isVisible={loading && uploadProgress > 0} />

      {/* Action Buttons */}
      <Box display="flex" gap={2} alignItems="center">
        <Button
          type="button"
          onClick={handleUploadClick}
          disabled={uploadButtonDisabled}
          variant="contained"
          color="primary"
          size="large"
        >
          {loading
            ? `Subiendo... (${Math.round(uploadProgress)}%)`
            : mediaFiles.length === 0
              ? maxFiles === 1
                ? 'Selecciona un archivo'
                : 'Selecciona archivos'
              : `Subir ${mediaFiles.length} archivo${mediaFiles.length !== 1 ? 's' : ''}`}
        </Button>

        {mediaFiles.length > 0 && !loading && (
          <Button variant="outlined" color="secondary" onClick={handleClearAll}>
            Limpiar todo
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={!!confirmDeleteId}
        onConfirm={confirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default DropzoneUploader;
